"use client";

/**
 * Drag-and-drop upload for the Drive browser.
 *
 * The interesting part is the GUARD, not the state: a drop is only meaningful in
 * the files view, when uploads are permitted, and when the account actually has
 * Drive access. A drop that slips past any of those posts files at a folder id
 * that may not exist.
 *
 * `onDragLeave` deliberately only clears when the event fired from the drop
 * container ITSELF (`currentTarget === target`) — a `dragleave` bubbling up from
 * a file row would otherwise flicker the highlight off the moment the pointer
 * crosses a child.
 */

import { useCallback, useState } from "react";

export interface UseGoogleDriveDragUploadOptions {
  /** All three conditions already resolved by the caller. */
  enabled: boolean;
  onFiles: (files: FileList) => void;
}

export interface GoogleDriveDragUpload {
  /** True only while a drag is over the zone AND the drop would be accepted. */
  showOverlay: boolean;
  onDragOver: (event: React.DragEvent) => void;
  onDragLeave: (event: React.DragEvent) => void;
  onDrop: (event: React.DragEvent) => void;
}

export function useGoogleDriveDragUpload({
  enabled,
  onFiles,
}: UseGoogleDriveDragUploadOptions): GoogleDriveDragUpload {
  const [isDragging, setIsDragging] = useState(false);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragging(false);
      if (!enabled) return;
      const files = event.dataTransfer?.files;
      if (files && files.length > 0) onFiles(files);
    },
    [enabled, onFiles]
  );

  const onDragOver = useCallback(
    (event: React.DragEvent) => {
      if (!enabled) return;
      event.preventDefault();
      setIsDragging((current) => (current ? current : true));
    },
    [enabled]
  );

  const onDragLeave = useCallback((event: React.DragEvent) => {
    // Only clear when leaving the drop container itself, not its children.
    if (event.currentTarget === event.target) setIsDragging(false);
  }, []);

  return { showOverlay: isDragging && enabled, onDragOver, onDragLeave, onDrop };
}
